/**
 * The live-breaks widget's reads (docs/live-breaks.md, "Storefront"; boxed's
 * `LiveBreaksDock.tsx` and the hooks under `live-breaks-widget/`), each polled every minute and on
 * focus, and the rows they make (`lib/liveBreaksWidget.ts` over the design system's builders):
 *
 * - sponsored live breaks: GET /v1/live-breaks/sponsored-streams, then the open live breaks on
 *   their channels (`listByChannel`);
 * - promoted streams: the same sponsored channels from Twitch (`getChannels`, one read for both),
 *   live with no live break;
 * - the queue Return row: every entry the shopper has queued (`listMyQueuedEntries`);
 * - the hosting Return row: their open live breaks alone (`listHosting`), never the host
 *   dashboard's figures, which every signed-in shopper would otherwise pay for every minute;
 * - the pinned row: the live break last opened in the player (`lib/pip.ts`), read on its own;
 * - the picker's Recent group: the live breaks watched in the player lately, still open.
 *
 * The whole widget needs `features.liveBreaks`; everything Twitch (the sponsored, promoted and
 * pinned rows, the picker and the player) also needs the store's Twitch app, while the two Return
 * rows don't.
 */
import type { LiveBreakResponse } from "@loot-os/api-contract";
import { LootApiError } from "@loot-os/client";
import {
  hostingReturnRow,
  type LiveBreakWidgetStream,
  liveBreakWidgetStream,
  promotedWidgetStreams,
  queueReturnRow,
  sponsoredLiveBreakStreams,
} from "@loot-os/design-system/domain/liveBreaks/liveBreakWidget";
import { type UseQueryResult, useQueries, useQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import {
  liveBreakHref,
  liveBreakIdFromPath,
  NO_WIDGET_ROWS,
  pinnedWidgetStream,
  resolveWidgetRows,
  type WidgetRows,
} from "./liveBreaksWidget.js";
import {
  forgetRecentStreams,
  type PipRowsState,
  unpinStream,
  usePipRowsState,
  useRecentStreams,
} from "./pip.js";
import {
  invalidateLiveBreaksWidget,
  liveBreakHostOpenQueryOptions,
  liveBreakMyQueuedEntriesQueryOptions,
  liveBreakQueryOptions,
  liveBreakSponsoredStreamsQueryOptions,
  liveBreaksByChannelQueryOptions,
  lootMeQueryOptions,
  twitchChannelsQueryOptions,
  twitchLoginsOf,
  twitchMeQueryOptions,
} from "./queries.js";
import { useShopper } from "./shopper.js";
import { useTenant } from "./tenant.js";
import { twitchOf } from "./twitch.js";

/** How often each of the widget's reads runs while the tab is visible (and on every focus). */
export const LIVE_BREAKS_WIDGET_POLL_MS = 60_000;

const POLL = {
  refetchInterval: LIVE_BREAKS_WIDGET_POLL_MS,
  refetchOnWindowFocus: "always",
} as const;

/** Everything the dock draws. */
export interface LiveBreaksWidgetState {
  /** `features.liveBreaks`: nothing at all shows without it. */
  enabled: boolean;
  /** The store's Twitch app: the stream rows and the player need it. */
  twitch: boolean;
  /** The live break whose page is showing; the widget's rows hide there, the player stays. */
  activeLiveBreakId: string | null;
  pip: PipRowsState;
  rows: WidgetRows;
}

/** The widget's rows and the player's state, from every read the widget makes. */
export function useLiveBreaksWidget(): LiveBreaksWidgetState {
  const { config, hasFeature } = useTenant();
  const { user, sessionToken } = useShopper();
  const enabled = hasFeature("liveBreaks");
  const twitch = enabled && twitchOf(config).enabled;
  const signedIn = !!user && !!sessionToken;
  const houseName = config.name;
  const pathname = useLocation({ select: (location) => location.pathname });
  const activeLiveBreakId = enabled ? liveBreakIdFromPath(pathname) : null;
  const onPage = activeLiveBreakId !== null;
  const pip = usePipRowsState();
  const recent = useRecentStreams();

  // ---- sponsored live breaks and promoted streams (Twitch) ----------------------------------------
  const sponsoredRead = useQuery({
    ...liveBreakSponsoredStreamsQueryOptions(),
    ...POLL,
    enabled: twitch,
  });
  const sponsoredStreams = sponsoredRead.data?.data;
  const sponsoredLogins = useMemo(
    () => twitchLoginsOf((sponsoredStreams ?? []).map((stream) => stream.twitchLogin)),
    [sponsoredStreams],
  );
  const byChannel = useQuery({
    ...liveBreaksByChannelQueryOptions(sponsoredLogins),
    ...POLL,
    enabled: twitch && sponsoredLogins.length > 0,
  });
  // Every sponsored channel and the pinned one, in one read a minute: the rows' avatars, and
  // which sponsored streamers are live.
  const pinnedChannel = pip.pinned?.channel ?? null;
  const channelLogins = useMemo(
    () => twitchLoginsOf([...sponsoredLogins, ...(pinnedChannel ? [pinnedChannel] : [])]),
    [sponsoredLogins, pinnedChannel],
  );
  const channels = useQuery({
    ...twitchChannelsQueryOptions(channelLogins),
    ...POLL,
    enabled: twitch && channelLogins.length > 0,
  });
  const twitchMe = useQuery({
    ...twitchMeQueryOptions(),
    enabled: twitch && signedIn && sponsoredLogins.length > 0,
  });
  const lootMe = useQuery({ ...lootMeQueryOptions(), enabled: twitch && signedIn });

  // ---- the shopper's own: queued entries and hosted live breaks ---------------------------------
  const queued = useQuery({
    ...liveBreakMyQueuedEntriesQueryOptions(),
    ...POLL,
    enabled: enabled && signedIn,
  });
  const hosting = useQuery({
    ...liveBreakHostOpenQueryOptions(),
    ...POLL,
    enabled: enabled && signedIn,
  });

  // A purchase, a Leave queue or an End on a live break's page changes the Return rows: read them
  // again the moment the shopper leaves it (for another page, or another live break's).
  const previousPage = useRef(activeLiveBreakId);
  useEffect(() => {
    const before = previousPage.current;
    previousPage.current = activeLiveBreakId;
    if (before !== null && before !== activeLiveBreakId) invalidateLiveBreaksWidget();
  }, [activeLiveBreakId]);

  // ---- the pinned live break ----------------------------------------------------------------------
  const pinnedId = twitch ? (pip.pinned?.liveBreakId ?? null) : null;
  const pinnedRead = useQuery({
    ...liveBreakQueryOptions(pinnedId ?? ""),
    ...POLL,
    staleTime: 30_000,
    enabled: pinnedId !== null && !onPage,
  });
  const pinnedAvatar = channels.data?.find((channel) => channel.login === pinnedChannel)?.avatarUrl;
  const pinned = pinnedWidgetStream({
    pinned: twitch ? pip.pinned : null,
    read: readOf(pinnedRead),
    houseName,
    avatarSrc: pinnedAvatar,
  });
  useEffect(() => {
    if (pinned.gone) unpinStream();
  }, [pinned.gone]);

  // ---- the recently watched, still open, never the shopper's own -------------------------------
  const recentReads = useQueries({
    queries: recent.map((stream) => ({
      ...liveBreakQueryOptions(stream.liveBreakId ?? ""),
      staleTime: 30_000,
      refetchInterval: LIVE_BREAKS_WIDGET_POLL_MS,
      refetchOnWindowFocus: false,
      enabled: twitch && !onPage && stream.liveBreakId !== null,
    })),
  });
  const closedRecent = recent.flatMap((stream, i) => {
    const read = recentReads[i] ? readOf(recentReads[i]) : undefined;
    const gone = read === "missing" || read?.status === "closed";
    return stream.liveBreakId && gone ? [stream.liveBreakId] : [];
  });
  const closedKey = closedRecent.join(",");
  useEffect(() => {
    if (closedKey) forgetRecentStreams(closedKey.split(","));
  }, [closedKey]);
  const recentRows = recent.flatMap((stream, i): LiveBreakWidgetStream[] => {
    const read = recentReads[i] ? readOf(recentReads[i]) : undefined;
    if (!stream.liveBreakId || read === "missing" || read === undefined) return [];
    if (read.status === "closed" || read.isViewerHost) return [];
    const row = liveBreakWidgetStream(read, { href: liveBreakHref(read.id), houseName });
    // The picker's Recent says "Live break", not the phase.
    return row ? [{ ...row, liveBreak: row.liveBreak && { ...row.liveBreak, phase: null } }] : [];
  });

  // ---- the rows -------------------------------------------------------------------------------------
  const sponsoredInput = {
    sponsoredStreams: sponsoredStreams ?? [],
    liveBreaks: byChannel.data ?? [],
    channels: channels.data ?? [],
    href: liveBreakHref,
    houseName,
  };
  const sponsored = twitch
    ? sponsoredLiveBreakStreams({ ...sponsoredInput, viewerEndUserId: lootMe.data?.id ?? null })
    : [];
  const promoted = twitch
    ? promotedWidgetStreams({
        sponsoredStreams: sponsoredStreams ?? [],
        channels: channels.data ?? [],
        // A streamer with a sponsored live break is never promoted, the shopper's own included.
        hostingChannels: sponsoredLiveBreakStreams({
          ...sponsoredInput,
          viewerEndUserId: null,
        }).map((stream) => stream.channel),
        ownLogin: twitchMe.data?.identity?.login ?? null,
      })
    : [];
  const rows = enabled
    ? resolveWidgetRows({
        activeLiveBreakId,
        pipOpen: twitch && pip.stream !== null,
        streamRowHidden: pip.streamRowHidden,
        hosting: signedIn
          ? hostingReturnRow({
              host: { openLiveBreaks: hosting.data ?? [] },
              href: liveBreakHref,
              houseName,
            })
          : null,
        queue: signedIn
          ? queueReturnRow({ entries: queued.data ?? [], href: liveBreakHref, houseName })
          : null,
        pinned: pinned.stream,
        sponsored,
        promoted,
        pickedChannel: pip.selectedPromotedChannel,
        recent: twitch ? recentRows : [],
      })
    : NO_WIDGET_ROWS;

  return { enabled, twitch, activeLiveBreakId, pip, rows };
}

/** One live break's read as the widget uses it: the live break, `missing` on a 404, or unread. */
function readOf(
  query: Pick<UseQueryResult<LiveBreakResponse>, "data" | "error">,
): LiveBreakResponse["liveBreak"] | "missing" | undefined {
  if (query.error instanceof LootApiError && query.error.status === 404) return "missing";
  return query.data?.liveBreak;
}
